import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import ToggleSwitch from './ToggleSwitch';
import './FilterModal.css';

const HobbiesModal = ({ show, onHide, values, onUpdate }) => {
  const [selectedHobbies, setSelectedHobbies] = useState({
    outdoor_and_sports_hobbies: values.outdoor_and_sports_hobbies || [],
    artistic_and_creative_hobbies: values.artistic_and_creative_hobbies || [],
    intellectual_and_educational_hobbies: values.intellectual_and_educational_hobbies || [],
    culinary_and_lifestyle_hobbies: values.culinary_and_lifestyle_hobbies || [],
    fitness_and_wellness_hobbies: values.fitness_and_wellness_hobbies || [],
    adventure_and_exploration_hobbies: values.adventure_and_exploration_hobbies || [],
    technology_and_gaming_hobbies: values.technology_and_gaming_hobbies || [],
    social_and_community_hobbies: values.social_and_community_hobbies || []
  });
  const [showOthers, setShowOthers] = useState(false);

  const hobbyCategories = {
    outdoor_and_sports_hobbies: [
      'Swimming', 'Basketball', 'Soccer', 'Football', 'Tennis', 'Volleyball',
      'Baseball', 'Cycling', 'Running', 'Hiking', 'Skiing', 'Snowboarding',
      'Rock climbing', 'Surfing', 'Skateboarding', 'Martial arts', 'Yoga',
      'Golf', 'Archery', 'Badminton', 'Table tennis', 'Cricket', 'Horseback',
      'Riding', 'Fishing', 'Kayaking', 'Sailing', 'Canoeing', 'CrossFit',
      'Fencing', 'Softball', 'Hunting'
    ],
    artistic_and_creative_hobbies: [
      'Painting', 'Drawing', 'Photography', 'Sculpting', 'Pottery',
      'Calligraphy', 'Graphic design', 'Animation', 'Writing', 'Poetry',
      'Creative writing', 'Acting', 'Singing', 'Gardening', 'Music production',
      'Film making', 'Knitting', 'Dancing', 'Woodworking', 'Sewing',
      'Crafting', 'Origami', 'Playing musical instruments'
    ],
    intellectual_and_educational_hobbies: [
      'Reading', 'Puzzles', 'Chess', 'Learning new languages', 'Astronomy',
      'Coding/Programming', 'Geocaching', 'History research', 'Debate',
      'Philosophy', 'Public service', 'Public speaking', 'Watching documentaries',
      'Volunteering'
    ],
    culinary_and_lifestyle_hobbies: [
      'Cooking', 'Baking', 'Mixology', 'Wine tasting',
      'Gardening (Herb, Vegetable, Or Flower)', 'Homebrewing', 'Fermenting',
      'Traveling', 'Collecting (Ex: Stamps, Coins)', 'DIY projects',
      'Home improvement', 'Interior design', 'Animal care (Pet Sitting, Dog Walking)',
      'Potluck organizing'
    ],
    fitness_and_wellness_hobbies: [
      'Meditation', 'Pilates', 'Martial arts (Ex: Karate, Taekwondo, Judo)',
      'Aerobics', 'Zumba', 'Crossfit', 'Weightlifting', 'Stretching',
      'Mindfulness practices', 'Aromatherapy', 'Nutrition planning',
      'Health & wellness coaching', 'Jogging/Running'
    ],
    adventure_and_exploration_hobbies: [
      'Camping', 'Traveling', 'Backpacking', 'Road tripping', 'Hiking',
      'Geocaching', 'Drone flying', 'Photography (Travel And Nature)',
      'Birdwatching', 'Urban exploration', 'Wildlife observation'
    ],
    technology_and_gaming_hobbies: [
      'Video gaming', 'Virtual reality gaming', 'Esports', 'Drones',
      'App development', 'Game development', 'Robotics', '3D printing',
      'Augmented reality', 'VR/AR exploration', 'Electronic music production'
    ],
    social_and_community_hobbies: [
      'Meetup groups', 'Charity work', 'Networking', 'Organizing events',
      'Blogging/Vlogging', 'Public speaking', 'Event planning', 'Book clubs',
      'Debate teams', 'Hosting dinner parties',
      'Social media (Content Creation, Influencing)', 'Mentoring'
    ]
  };

  const handleHobbyToggle = (category, hobby) => {
    setSelectedHobbies(prev => ({
      ...prev,
      [category]: prev[category].includes(hobby)
        ? prev[category].filter(h => h !== hobby)
        : [...prev[category], hobby]
    }));
  };

  const handleApply = () => {
    Object.keys(selectedHobbies).forEach(key => {
      onUpdate(key, selectedHobbies[key]);
    });
    onHide();
  };

  const handleClear = () => {
    const clearedHobbies = Object.keys(selectedHobbies).reduce((acc, key) => {
      acc[key] = [];
      return acc;
    }, {});
    setSelectedHobbies(clearedHobbies);
    Object.keys(clearedHobbies).forEach(key => {
      onUpdate(key, []);
    });
    onHide();
  };

  const renderHobbySection = (category, title) => (
    <div className="hobby-section mb-4">
      <h6 className="hobby-section-title">{title}</h6>
      <div className="filter-checkbox-group">
        {hobbyCategories[category].map((hobby) => (
          <div
            key={hobby}
            className={`filter-checkbox-option ${selectedHobbies[category].includes(hobby) ? 'selected' : ''}`}
            onClick={(e) => {
              // Only handle click if it's not on the input itself
              if (e.target.type !== 'checkbox') {
                handleHobbyToggle(category, hobby);
              }
            }}
          >
            <input
              type="checkbox"
              id={`${category}-${hobby}`}
              checked={selectedHobbies[category].includes(hobby)}
              onChange={(e) => {
                e.stopPropagation();
                handleHobbyToggle(category, hobby);
              }}
            />
            <label htmlFor={`${category}-${hobby}`}>{hobby}</label>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} centered className="filter-modal" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Their Hobbies and Interests?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderHobbySection('outdoor_and_sports_hobbies', 'Outdoor and Sports Hobbies')}
        {renderHobbySection('artistic_and_creative_hobbies', 'Artistic and Creative Hobbies')}
        {renderHobbySection('intellectual_and_educational_hobbies', 'Intellectual and Educational Hobbies')}
        {renderHobbySection('culinary_and_lifestyle_hobbies', 'Culinary and Lifestyle Hobbies')}
        {renderHobbySection('fitness_and_wellness_hobbies', 'Fitness and Wellness Hobbies')}
        {renderHobbySection('adventure_and_exploration_hobbies', 'Adventure and Exploration Hobbies')}
        {renderHobbySection('technology_and_gaming_hobbies', 'Technology and Gaming Hobbies')}
        {renderHobbySection('social_and_community_hobbies', 'Social and Community Hobbies')}
        
        {/* Toggle Switch */}
        <ToggleSwitch 
          checked={showOthers} 
          onChange={setShowOthers}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClear} className="filter-modal-btn secondary">
          Clear
        </Button>
        <Button variant="primary" onClick={handleApply} className="filter-modal-btn primary">
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HobbiesModal;
